import { useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import type { Branch } from '../../types/domain';
import {
  type BranchSelection,
  isValidPromptPayAccount,
  type PromptPayAccountInput,
  type PromptPayAccountRow,
} from './paymentSettingsModel';

const SAVE_ERROR = 'บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง';

function BranchScopeField({
  branches,
  value,
  allowAll,
  allHint,
  disabled,
  onChange,
}: {
  branches: readonly Branch[];
  value: BranchSelection;
  allowAll: boolean;
  allHint: string;
  disabled: boolean;
  onChange: (next: BranchSelection) => void;
}) {
  const toggleBranch = (branchId: string, checked: boolean) =>
    onChange({
      ...value,
      branchIds: checked
        ? [...value.branchIds, branchId]
        : value.branchIds.filter((id) => id !== branchId),
    });

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-1 font-medium text-ink-1">ใช้ที่สาขาไหน?</legend>
      {allowAll ? (
        <>
          <label className="flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name="payment-branch-scope"
              checked={value.all}
              onChange={() => onChange({ ...value, all: true })}
            />
            ทุกสาขา
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name="payment-branch-scope"
              checked={!value.all}
              onChange={() => onChange({ ...value, all: false })}
            />
            เฉพาะบางสาขา
          </label>
        </>
      ) : null}
      {allowAll && value.all ? (
        <p className="text-sm-label text-ink-3">{allHint}</p>
      ) : (
        <div className="grid gap-1 rounded-card bg-surface-2 p-3 sm:grid-cols-2">
          {branches.map((branch) => (
            <label key={branch.id} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                aria-label={`ใช้ที่สาขา ${branch.short}`}
                checked={value.branchIds.includes(branch.id)}
                onChange={(event) =>
                  toggleBranch(branch.id, event.target.checked)
                }
              />
              {branch.short}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function DialogFooter({
  saving,
  canSave,
  saveLabel,
  onClose,
  onSave,
}: {
  saving: boolean;
  canSave: boolean;
  saveLabel: string;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Btn disabled={saving} onClick={onClose}>
        ยกเลิก
      </Btn>
      <Btn kind="primary" disabled={!canSave || saving} onClick={onSave}>
        {saving ? 'กำลังบันทึก…' : saveLabel}
      </Btn>
    </div>
  );
}

const isSelectionUsable = (selection: BranchSelection): boolean =>
  selection.all || selection.branchIds.length > 0;

export function PromptPayDialog({
  row,
  branches,
  canManageAllBranches,
  hasOtherAccounts,
  saving,
  failed,
  onClose,
  onSave,
}: {
  row: PromptPayAccountRow | null;
  branches: readonly Branch[];
  canManageAllBranches: boolean;
  hasOtherAccounts: boolean;
  saving: boolean;
  failed: boolean;
  onClose: () => void;
  onSave: (account: PromptPayAccountInput, selection: BranchSelection) => void;
}) {
  const [promptpayIdType, setPromptpayIdType] = useState(
    row?.account.promptpayIdType ?? 'mobile',
  );
  const [promptpayId, setPromptpayId] = useState(
    row?.account.promptpayId ?? '',
  );
  const [accountName, setAccountName] = useState(
    row?.account.accountName ?? '',
  );
  const [selection, setSelection] = useState<BranchSelection>({
    all:
      canManageAllBranches &&
      (!row ||
        (row.source === 'store' && row.branchIds.length === branches.length)),
    branchIds:
      row?.branchIds ?? (branches.length === 1 ? [branches[0].id] : []),
  });

  const isNationalId = promptpayIdType === 'national_id';
  const idLabel = isNationalId ? 'เลขบัตรประชาชน' : 'เบอร์โทรศัพท์พร้อมเพย์';
  const idValid = isValidPromptPayAccount({ promptpayId, promptpayIdType });
  const canSave =
    idValid && Boolean(accountName.trim()) && isSelectionUsable(selection);

  return (
    <Modal
      open
      title={row ? 'แก้ไขบัญชีพร้อมเพย์' : 'เพิ่มบัญชีพร้อมเพย์'}
      description="ลูกค้าสแกน QR ตามยอดขาย แล้วพนักงานตรวจยอดเงินเข้าบัญชีนี้เอง"
      onClose={() => {
        if (!saving) onClose();
      }}
      footer={
        <DialogFooter
          saving={saving}
          canSave={canSave}
          saveLabel="บันทึกบัญชี"
          onClose={onClose}
          onSave={() =>
            onSave(
              {
                promptpayId: promptpayId.trim(),
                promptpayIdType,
                accountName: accountName.trim(),
              },
              selection,
            )
          }
        />
      }
    >
      <div className="space-y-5">
        <Field label="ประเภทหมายเลขพร้อมเพย์" required>
          <Select
            aria-label="ประเภทหมายเลขพร้อมเพย์"
            value={promptpayIdType}
            disabled={saving}
            onChange={(event) =>
              setPromptpayIdType(
                event.target.value as PromptPayAccountInput['promptpayIdType'],
              )
            }
          >
            <option value="mobile">เบอร์โทรศัพท์</option>
            <option value="national_id">เลขบัตรประชาชน</option>
          </Select>
        </Field>
        <Field
          label={idLabel}
          required
          error={
            promptpayId.trim() && !idValid
              ? isNationalId
                ? 'กรอกเลขบัตรประชาชน 13 หลัก'
                : 'กรอกเบอร์โทรศัพท์ให้ถูกต้อง เช่น 0812345678'
              : undefined
          }
        >
          <Input
            aria-label={idLabel}
            inputMode="numeric"
            maxLength={isNationalId ? 13 : 12}
            placeholder={isNationalId ? 'เลข 13 หลัก' : 'เช่น 0812345678'}
            value={promptpayId}
            disabled={saving}
            onChange={(event) => setPromptpayId(event.target.value)}
          />
        </Field>
        <Field
          label="ชื่อบัญชี"
          required
          hint="ชื่อที่แสดงให้ลูกค้าตรวจก่อนโอน"
        >
          <Input
            aria-label="ชื่อบัญชี"
            value={accountName}
            disabled={saving}
            onChange={(event) => setAccountName(event.target.value)}
          />
        </Field>
        <BranchScopeField
          branches={branches}
          value={selection}
          allowAll={canManageAllBranches}
          allHint="สาขาที่เปิดใหม่ภายหลังจะใช้บัญชีนี้ด้วย"
          disabled={saving}
          onChange={setSelection}
        />
        {hasOtherAccounts ? (
          <p className="rounded-card bg-warning-50 p-3 text-sm-label text-ink-2">
            แต่ละสาขาใช้พร้อมเพย์ได้ 1 บัญชี
            สาขาที่เลือกจะเปลี่ยนมาใช้บัญชีนี้แทนบัญชีเดิม
          </p>
        ) : null}
        {failed ? (
          <p role="alert" className="text-danger">
            {SAVE_ERROR}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export function ManualChannelDialog({
  initialName,
  initialBranchIds,
  isEdit,
  branches,
  canManageAllBranches,
  saving,
  failed,
  onClose,
  onSave,
}: {
  initialName: string;
  initialBranchIds: string[];
  isEdit: boolean;
  branches: readonly Branch[];
  canManageAllBranches: boolean;
  saving: boolean;
  failed: boolean;
  onClose: () => void;
  onSave: (name: string, branchIds: string[]) => void;
}) {
  const [name, setName] = useState(initialName);
  const [selection, setSelection] = useState<BranchSelection>({
    all:
      canManageAllBranches &&
      (!isEdit || initialBranchIds.length === branches.length),
    branchIds:
      isEdit || branches.length !== 1 ? initialBranchIds : [branches[0].id],
  });
  // A manager has no archive action, so unticking is how a branch turns it off.
  const canClearBranches = isEdit && !canManageAllBranches;
  const canSave =
    Boolean(name.trim()) && (canClearBranches || isSelectionUsable(selection));

  return (
    <Modal
      open
      title={isEdit ? 'แก้ไขวิธีชำระเงิน' : 'เพิ่มวิธีชำระเงินอื่น'}
      description="พนักงานตรวจการรับเงินเอง ระบบไม่ได้เชื่อมต่อผู้ให้บริการรับเงินให้"
      onClose={() => {
        if (!saving) onClose();
      }}
      footer={
        <DialogFooter
          saving={saving}
          canSave={canSave}
          saveLabel="บันทึก"
          onClose={onClose}
          onSave={() =>
            onSave(
              name.trim(),
              selection.all
                ? branches.map((branch) => branch.id)
                : selection.branchIds,
            )
          }
        />
      }
    >
      <div className="space-y-5">
        <Field
          label="ชื่อวิธีชำระเงิน"
          required
          hint="ชื่อนี้จะแสดงที่หน้าจอ POS และในรายงาน"
        >
          <Input
            autoFocus
            aria-label="ชื่อวิธีชำระเงิน"
            placeholder="เช่น บัตรสวัสดิการแห่งรัฐ"
            value={name}
            disabled={saving || (isEdit && !canManageAllBranches)}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <BranchScopeField
          branches={branches}
          value={selection}
          allowAll={canManageAllBranches}
          allHint="เปิดให้ทุกสาขาที่มีตอนนี้ สาขาที่เปิดใหม่ต้องมาเลือกเพิ่มที่นี่"
          disabled={saving}
          onChange={setSelection}
        />
        {failed ? (
          <p role="alert" className="text-danger">
            {SAVE_ERROR} ระบบจะใช้รายการเดิมโดยไม่สร้างซ้ำ
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
